/**
 * TeamTalkInbox — /team-messages (V1.7.9 refactored)
 *
 * V1.7.4 — 1:1 DMs; V1.7.6 — Groups + @mentions + attachments;
 * V1.7.7 — Read receipts + typing; V1.7.8 — WhatsApp-style attachments;
 * V1.7.9 — Split into /components/teamtalk/ modules.
 *
 * This file is now a thin orchestrator (~300 lines). All heavy UI lives
 * under /components/teamtalk/:
 *   - helpers.js               → formatWhen, roleLabel, computeReadReceipt, constants
 *   - AttachmentBubble.jsx     → image/video/doc bubbles + ImageLightbox
 *   - MsgBubble.jsx            → MsgBubble + ReadTick + TypingIndicator
 *   - ThreadRow.jsx            → ThreadRow + ConvoHeader
 *   - ComposeModals.jsx        → ComposeModal + GroupComposeModal + ManageGroupModal
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    PaperPlaneTilt,
    Plus,
    Users,
    UsersThree,
    Paperclip,
    At,
} from "@phosphor-icons/react";
import {
    REFRESH_INTERVAL_MS,
    MAX_ATTACHMENT_MB,
    TYPING_PING_INTERVAL_MS,
    TYPING_EXPIRE_MS,
    computeReadReceipt,
    roleLabel,
} from "@/components/teamtalk/helpers";
import { MsgBubble, TypingIndicator } from "@/components/teamtalk/MsgBubble";
import { ThreadRow, ConvoHeader } from "@/components/teamtalk/ThreadRow";
import PushOptInBanner from "@/components/PushOptInBanner";
import { VoiceRecorder } from "@/components/chat/VoiceRecorder";
import { ReplyPreviewBar } from "@/components/chat/SwipeToReply";
import { useChatDensity, ChatDensityToggle } from "@/lib/useChatDensity";
import ForwardMessageModal from "@/components/chat/ForwardMessageModal";
import FilterChips from "@/components/chat/FilterChips";
import {
    ComposeModal,
    GroupComposeModal,
    ManageGroupModal,
} from "@/components/teamtalk/ComposeModals";

export default function TeamTalkInbox() {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const [threads, setThreads] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [convo, setConvo] = useState(null);
    const [density, setDensity] = useChatDensity();
    const [loading, setLoading] = useState(true);
    const [reply, setReply] = useState("");
    const [mentionOpen, setMentionOpen] = useState(false);
    const [mentionQ, setMentionQ] = useState("");
    const [mentionRefs, setMentionRefs] = useState([]);
    const [sending, setSending] = useState(false);
    const [uploadBusy, setUploadBusy] = useState(false);
    const [composeOpen, setComposeOpen] = useState(false);
    const [groupComposeOpen, setGroupComposeOpen] = useState(false);
    const [composeContext, setComposeContext] = useState(null);
    const [manageOpen, setManageOpen] = useState(false);
    const [typingByThread, setTypingByThread] = useState({});
    const [replyTo, setReplyTo] = useState(null);  // V1.9.27
    const [forwardMsg, setForwardMsg] = useState(null);  // V1.9.29
    const [inboxFilter, setInboxFilter] = useState("all");  // V1.9.32: all|unread|groups|starred
    const [starredMap, setStarredMap] = useState({});  // threadId → count of starred msgs in that thread
    const lastTypingPingRef = useRef(0);
    const fileInputRef = useRef();
    const replyInputRef = useRef();
    const pollRef = useRef();

    // V1.8.1 — cross-context handoff from DaddyTalk.
    // URL: /messages?tab=team&compose=dm|group&candidate_id=X&candidate_name=Y
    useEffect(() => {
        const mode = params.get("compose");
        if (!mode) return;
        const cid = params.get("candidate_id");
        const cname = params.get("candidate_name");
        const ctx = cid ? {
            type: "candidate",
            id: cid,
            label: cname || undefined,
            body: cname ? `Sharing context on ${cname}. ` : "",
        } : null;
        setComposeContext(ctx);
        if (mode === "group") setGroupComposeOpen(true);
        else setComposeOpen(true);
        // Clean the compose params so a refresh doesn't reopen the modal.
        const next = new URLSearchParams(params);
        next.delete("compose");
        next.delete("candidate_id");
        next.delete("candidate_name");
        setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadThreads = useCallback(async () => {
        try {
            const res = await api.get("/team-chat/threads");
            setThreads(res?.data?.items || []);
        } catch (err) {
            if (err?.response?.status !== 401) console.error("TeamTalk inbox load failed", err);
        } finally { setLoading(false); }
    }, []);

    const loadConvo = useCallback(async (id) => {
        if (!id) { setConvo(null); return; }
        try {
            const res = await api.get(`/team-chat/threads/${id}`);
            setConvo(res?.data || null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load conversation");
        }
    }, []);

    useEffect(() => {
        loadThreads();
        pollRef.current = setInterval(() => {
            if (document.visibilityState === "visible") {
                loadThreads();
                if (selectedId) loadConvo(selectedId);
            }
        }, REFRESH_INTERVAL_MS);
        return () => clearInterval(pollRef.current);
    }, [loadThreads, loadConvo, selectedId]);

    useEffect(() => { loadConvo(selectedId); }, [selectedId, loadConvo]);

    // SSE bridges (features.jsx rebroadcasts as window events)
    useEffect(() => {
        const onMessageNew = (ev) => {
            const detail = ev?.detail || {};
            if (detail.thread_id === selectedId) loadConvo(selectedId);
            loadThreads();
        };
        const onThreadUpdated = (ev) => {
            const detail = ev?.detail || {};
            if (detail.thread_id === selectedId) loadConvo(selectedId);
            loadThreads();
        };
        const onTyping = (ev) => {
            const { thread_id, user_id, user_name, is_typing } = ev?.detail || {};
            if (!thread_id || !user_id || user_id === user?.id) return;
            setTypingByThread((prev) => {
                const forThread = { ...(prev[thread_id] || {}) };
                if (is_typing) {
                    forThread[user_id] = { user_name, expires_at: Date.now() + TYPING_EXPIRE_MS };
                } else {
                    delete forThread[user_id];
                }
                return { ...prev, [thread_id]: forThread };
            });
        };
        const onRead = (ev) => {
            const detail = ev?.detail || {};
            if (detail.thread_id === selectedId) loadConvo(selectedId);
        };
        const onReacted = (ev) => {
            const detail = ev?.detail || {};
            if (detail.thread_id !== selectedId) return;
            setConvo((prev) => prev ? ({
                ...prev,
                messages: prev.messages.map((m) =>
                    m.id === detail.message_id ? { ...m, reactions: detail.reactions } : m,
                ),
            }) : prev);
        };
        window.addEventListener("teamtalk:message-new", onMessageNew);
        window.addEventListener("teamtalk:thread-updated", onThreadUpdated);
        window.addEventListener("teamtalk:typing", onTyping);
        window.addEventListener("teamtalk:read", onRead);
        window.addEventListener("teamtalk:message-reacted", onReacted);
        const onUpdated = (ev) => {
            const detail = ev?.detail || {};
            if (detail.thread_id !== selectedId) return;
            setConvo((prev) => prev ? ({
                ...prev,
                messages: prev.messages.map((m) => {
                    if (m.id !== detail.message_id) return m;
                    const attPatch = detail.attachment || {};
                    return { ...m, attachment: { ...(m.attachment || {}), ...attPatch } };
                }),
            }) : prev);
        };
        window.addEventListener("teamtalk:message-updated", onUpdated);
        return () => {
            window.removeEventListener("teamtalk:message-new", onMessageNew);
            window.removeEventListener("teamtalk:thread-updated", onThreadUpdated);
            window.removeEventListener("teamtalk:typing", onTyping);
            window.removeEventListener("teamtalk:read", onRead);
            window.removeEventListener("teamtalk:message-reacted", onReacted);
            window.removeEventListener("teamtalk:message-updated", onUpdated);
        };
    }, [loadThreads, loadConvo, selectedId, user?.id]);

    // Sweep expired typing indicators every 2s.
    useEffect(() => {
        const t = setInterval(() => {
            setTypingByThread((prev) => {
                const now = Date.now();
                let changed = false;
                const next = {};
                for (const [tid, byUid] of Object.entries(prev)) {
                    const filtered = {};
                    for (const [uid, info] of Object.entries(byUid)) {
                        if (info.expires_at > now) filtered[uid] = info;
                        else changed = true;
                    }
                    if (Object.keys(filtered).length > 0) next[tid] = filtered;
                }
                return changed ? next : prev;
            });
        }, 2000);
        return () => clearInterval(t);
    }, []);

    // Auto-mark-read on open (server also broadcasts SSE for peers).
    useEffect(() => {
        if (!selectedId) return;
        api.post(`/team-chat/threads/${selectedId}/read`).catch(() => { /* silent */ });
    }, [selectedId, convo?.messages?.length]);

    const unreadCount = useMemo(
        () => threads.filter((t) => t.unread).length, [threads],
    );

    // V1.9.32 — starred-thread lookup for filter chip
    const [starredThreadIds, setStarredThreadIds] = useState(new Set());
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/team-chat/starred");
                if (cancelled) return;
                const s = new Set((r.data.items || []).map((m) => m.thread_id));
                setStarredThreadIds(s);
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, [convo?.messages?.length]); // refresh when a convo message list changes (star toggled)

    const filteredThreads = useMemo(() => {
        if (inboxFilter === "unread") return threads.filter((t) => t.unread);
        if (inboxFilter === "groups") return threads.filter((t) => t.is_group);
        if (inboxFilter === "starred") return threads.filter((t) => starredThreadIds.has(t.id));
        return threads;
    }, [threads, inboxFilter, starredThreadIds]);
    const groupCount = useMemo(() => threads.filter((t) => t.is_group).length, [threads]);

    const isGroup = Boolean(convo?.thread?.is_group);
    const otherMembers = useMemo(
        () => (convo?.members || []).filter((m) => m.id !== user?.id),
        [convo, user],
    );

    function pingTyping(is_typing) {
        if (!selectedId) return;
        api.post(`/team-chat/threads/${selectedId}/typing`, { is_typing }).catch(() => { /* silent */ });
    }

    async function handleReact(messageId, emoji) {
        // Optimistic toggle for snappy UX.
        setConvo((prev) => {
            if (!prev) return prev;
            const next = { ...prev, messages: prev.messages.map((m) => {
                if (m.id !== messageId) return m;
                const rx = { ...(m.reactions || {}) };
                const list = new Set(rx[emoji] || []);
                if (list.has(user?.id)) list.delete(user?.id); else list.add(user?.id);
                if (list.size === 0) delete rx[emoji]; else rx[emoji] = [...list];
                return { ...m, reactions: rx };
            }) };
            return next;
        });
        try {
            await api.post(`/team-chat/messages/${messageId}/react`, { emoji });
        } catch (err) {
            toast.error("Couldn't save reaction. Please retry.");
            loadConvo(selectedId);
        }
    }

    async function handleStar(messageId) {
        // V1.9.32 — optimistic toggle
        setConvo((prev) => {
            if (!prev) return prev;
            return { ...prev, messages: prev.messages.map((m) => {
                if (m.id !== messageId) return m;
                const arr = new Set(m.starred_by_user_ids || []);
                if (arr.has(user?.id)) arr.delete(user?.id); else arr.add(user?.id);
                return { ...m, starred_by_user_ids: [...arr] };
            }) };
        });
        try {
            await api.post(`/team-chat/messages/${messageId}/star`);
        } catch (err) {
            toast.error("Couldn't toggle star. Please retry.");
            loadConvo(selectedId);
        }
    }

    function handleReplyChange(e) {
        const val = e.target.value;
        setReply(val);
        if (val.trim()) {
            const now = Date.now();
            if (now - lastTypingPingRef.current > TYPING_PING_INTERVAL_MS) {
                lastTypingPingRef.current = now;
                pingTyping(true);
            }
        }
        if (!isGroup) { setMentionOpen(false); return; }
        const caret = e.target.selectionStart || val.length;
        const upto = val.slice(0, caret);
        const m = upto.match(/(^|\s)@([\w-]*)$/);
        if (m) {
            setMentionQ(m[2]);
            setMentionOpen(true);
        } else {
            setMentionOpen(false);
        }
    }

    function pickMention(u) {
        const caret = replyInputRef.current?.selectionStart || reply.length;
        const upto = reply.slice(0, caret);
        const rest = reply.slice(caret);
        const replaced = upto.replace(/@([\w-]*)$/, `@${u.name} `);
        setReply(replaced + rest);
        setMentionRefs((prev) => (prev.find((x) => x.id === u.id) ? prev : [...prev, u]));
        setMentionOpen(false);
        setTimeout(() => replyInputRef.current?.focus(), 20);
    }

    const filteredMentionOptions = useMemo(() => {
        if (!isGroup || !mentionOpen) return [];
        const q = mentionQ.toLowerCase();
        // V2.0.35 (Feb 22, 2026) — synthetic `@all` option at the top of
        // the picker so anyone in a group can broadcast a push
        // notification to every member. Backend expands the `__all__`
        // sentinel in _notify_new_team_message.
        const allOpt = {
            id: "__all__",
            name: "all",
            _label: "@all — notify everyone in this group",
            _synthetic: true,
        };
        const matches = otherMembers
            .filter((m) => (m.name || "").toLowerCase().includes(q))
            .slice(0, 6);
        const showAll = "all".includes(q) || q === "";
        return showAll ? [allOpt, ...matches] : matches;
    }, [isGroup, mentionOpen, mentionQ, otherMembers]);

    async function sendReply() {
        const body = reply.trim();
        if (!body || !selectedId || sending) return;
        setSending(true);
        try {
            await api.post(`/team-chat/threads/${selectedId}/messages`, {
                body,
                mentioned_user_ids: mentionRefs.map((u) => u.id),
                ...(replyTo ? { reply_to_message_id: replyTo.id } : {}),
            });
            setReply("");
            setMentionRefs([]);
            setReplyTo(null);
            pingTyping(false);
            lastTypingPingRef.current = 0;
            await loadConvo(selectedId);
            await loadThreads();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send reply");
        } finally { setSending(false); }
    }

    async function uploadFile(file) {
        if (!file || !selectedId) return;
        if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
            toast.error(`File too large — max ${MAX_ATTACHMENT_MB} MB`);
            return;
        }
        setUploadBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("body", reply.trim());
            fd.append("mentioned_user_ids", mentionRefs.map((u) => u.id).join(","));
            if (replyTo) fd.append("reply_to_message_id", replyTo.id);
            await api.post(
                `/team-chat/threads/${selectedId}/messages/attachment`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setReply("");
            setMentionRefs([]);
            setReplyTo(null);
            await loadConvo(selectedId);
            await loadThreads();
            toast.success("Attachment sent");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploadBusy(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }

    async function sendVoice(blob, durationMs) {
        if (!blob || !selectedId || sending) return;
        setSending(true);
        try {
            const fd = new FormData();
            const ext = (blob.type.split("/")[1] || "webm").split(";")[0];
            fd.append("file", blob, `voice.${ext}`);
            fd.append("body", "");
            fd.append("duration_ms", String(durationMs || 0));
            if (replyTo) fd.append("reply_to_message_id", replyTo.id);
            await api.post(
                `/team-chat/threads/${selectedId}/messages/attachment`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setReplyTo(null);
            await loadConvo(selectedId);
            await loadThreads();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send voice note");
        } finally { setSending(false); }
    }

    async function handleThreadCreated(newThread) {
        setComposeOpen(false);
        setGroupComposeOpen(false);
        setComposeContext(null);
        await loadThreads();
        if (newThread?.id) setSelectedId(newThread.id);
    }

    return (
        <div className="flex-1 min-h-0 flex flex-col bg-[#f0f2f5]" data-testid="teamtalk-root">
            <PushOptInBanner user={user} context="team_chat" />
            <header className="bg-white border-b border-ink/10 px-4 sm:px-6 py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <Users size={18} weight="bold" className="text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <h1 className="text-sm font-semibold text-ink truncate" data-testid="teamtalk-title">
                        TeamTalk
                    </h1>
                    <p className="text-[11px] text-ink/50 truncate">
                        {unreadCount > 0 ? `${unreadCount} unread` : "Internal team chat"}
                    </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    <button
                        type="button"
                        onClick={() => setGroupComposeOpen(true)}
                        data-testid="teamtalk-new-group-btn"
                        aria-label="New group"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full text-ink/60 hover:bg-ink/5 hover:text-emerald-600 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 sm:gap-1.5 sm:text-xs sm:font-medium"
                    >
                        <UsersThree size={16} weight="bold" />
                        <span className="hidden sm:inline">New group</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setComposeOpen(true)}
                        data-testid="teamtalk-new-chat-btn"
                        aria-label="New chat"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 active:scale-95 transition sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 sm:gap-1.5 sm:text-xs sm:font-medium sm:rounded-full"
                    >
                        <Plus size={16} weight="bold" />
                        <span className="hidden sm:inline">New chat</span>
                    </button>
                </div>
            </header>

            <div className="flex-1 min-h-0 max-w-6xl w-full mx-auto grid grid-cols-1 md:grid-cols-[minmax(260px,340px)_1fr] gap-0">
                {/* Thread list */}
                <aside
                    className={`border-r border-ink/10 bg-white overflow-y-auto ${selectedId ? "hidden md:block" : "block"}`}
                    data-testid="teamtalk-thread-list"
                    data-density={density}
                >
                    {threads.length > 0 && (
                        <FilterChips
                            value={inboxFilter}
                            onChange={setInboxFilter}
                            testIdPrefix="teamtalk-filter"
                            chips={[
                                { value: "all", label: "All" },
                                { value: "unread", label: "Unread", count: unreadCount },
                                { value: "groups", label: "Groups", count: groupCount },
                                { value: "starred", label: "Starred", count: starredThreadIds.size },
                            ]}
                        />
                    )}
                    {loading ? (
                        <div className="p-6 text-xs text-ink/50">Loading…</div>
                    ) : threads.length === 0 ? (
                        <div className="p-6 text-center" data-testid="teamtalk-empty">
                            <div className="w-16 h-16 rounded-full bg-cobalt/10 mx-auto mb-3 flex items-center justify-center">
                                <Users size={32} weight="bold" className="text-cobalt" />
                            </div>
                            <p className="font-heading font-bold text-sm mb-1">No team chats yet</p>
                            <p className="text-xs text-ink/50 mb-4">
                                Start a DM or a group huddle with your teammates.
                            </p>
                            <button
                                type="button"
                                onClick={() => setComposeOpen(true)}
                                data-testid="teamtalk-empty-new-chat-btn"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-cobalt text-white text-[11px] font-black uppercase tracking-[0.2em] hover:bg-cobalt/90"
                            >
                                <Plus size={12} weight="bold" /> Start a chat
                            </button>
                        </div>
                    ) : filteredThreads.length === 0 ? (
                        <div className="p-6 text-center text-xs text-ink/50" data-testid="teamtalk-filter-empty">
                            No {inboxFilter} chats to show.
                        </div>
                    ) : filteredThreads.map((t) => (
                        <ThreadRow
                            key={t.id} t={t}
                            selected={selectedId === t.id}
                            onClick={() => setSelectedId(t.id)}
                        />
                    ))}
                </aside>

                {/* Conversation */}
                <section
                    className={`flex flex-col min-h-0 bg-cream/30 ${selectedId ? "block" : "hidden md:flex"}`}
                    data-testid="teamtalk-convo"
                >
                    {!selectedId ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                            <div className="w-24 h-24 rounded-full bg-cobalt/10 mb-4 flex items-center justify-center">
                                <Users size={48} weight="bold" className="text-cobalt" />
                            </div>
                            <p className="font-heading font-bold text-base mb-1">Pick a chat to open it</p>
                            <p className="text-xs text-ink/50">Internal team messages live here.</p>
                        </div>
                    ) : !convo ? (
                        <div className="flex-1 flex items-center justify-center text-xs text-ink/50">Loading…</div>
                    ) : (
                        <>
                            <ConvoHeader
                                convo={convo}
                                onBack={() => setSelectedId(null)}
                                onManage={() => setManageOpen(true)}
                                onImageChanged={(url) => {
                                    setConvo((prev) => prev ? { ...prev, thread: { ...prev.thread, image_url: url } } : prev);
                                    loadThreads();
                                }}
                                rightSlot={<ChatDensityToggle density={density} setDensity={setDensity} testid="teamtalk-density-toggle" />}
                            />

                            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 chat-surface" data-density={density} data-testid="teamtalk-messages">
                                {convo.messages.map((m, idx) => {
                                    const sender = (convo.members || []).find((x) => x.id === m.sender_id);
                                    const prev = convo.messages[idx - 1];
                                    const showAvatar = isGroup && m.sender_id !== user?.id && (
                                        !prev
                                        || prev.sender_id !== m.sender_id
                                        || prev.sender_role === "system"
                                    );
                                    // V2.0.20 — Always label the sender when a
                                    // message carries an attachment, so users
                                    // can tell who sent an image/file even in
                                    // the middle of a burst.
                                    const hasAttachment = !!m.attachment;
                                    const showSenderLabel = isGroup && m.sender_id !== user?.id && (showAvatar || hasAttachment);
                                    // V1.9.27 — quoted-message lookup for replies
                                    let replyQuoted = null;
                                    if (m.reply_to_message_id) {
                                        const src = convo.messages.find((x) => x.id === m.reply_to_message_id);
                                        if (src) {
                                            const srcSender = (convo.members || []).find((x) => x.id === src.sender_id);
                                            replyQuoted = {
                                                sender_name: src.sender_id === user?.id ? "You" : (srcSender?.name || "Teammate"),
                                                body: src.body,
                                                attachment_kind: src.attachment?.kind
                                                    || (src.attachment?.content_type?.startsWith("image/") && "image")
                                                    || (src.attachment?.content_type?.startsWith("video/") && "video")
                                                    || (src.attachment ? "file" : null),
                                            };
                                        }
                                    }
                                    return (
                                        <MsgBubble
                                            key={m.id} m={m}
                                            isMine={m.sender_id === user?.id}
                                            senderName={
                                                m.sender_id === user?.id
                                                    ? user?.name
                                                    : sender?.name
                                            }
                                            senderAvatarUrl={sender?.avatar_url}
                                            showAvatar={isGroup ? showAvatar : undefined}
                                            showSenderLabel={showSenderLabel}
                                            readReceipt={
                                                m.sender_id === user?.id
                                                    ? computeReadReceipt(m, convo, user?.id)
                                                    : null
                                            }
                                            myUserId={user?.id}
                                            onReact={handleReact}
                                            onStar={handleStar}
                                            onReply={(msg) => {
                                                const s = (convo.members || []).find((x) => x.id === msg.sender_id);
                                                setReplyTo({
                                                    id: msg.id,
                                                    sender_name: msg.sender_id === user?.id ? "You" : (s?.name || "Teammate"),
                                                    body: msg.body,
                                                    attachment_kind: msg.attachment?.kind
                                                        || (msg.attachment?.content_type?.startsWith("image/") && "image")
                                                        || (msg.attachment ? "file" : null),
                                                });
                                                setTimeout(() => replyInputRef.current?.focus(), 30);
                                            }}
                                            onForward={(msg) => setForwardMsg(msg)}
                                            replyQuoted={replyQuoted}
                                        />
                                    );
                                })}
                                <TypingIndicator
                                    typingByUser={typingByThread[selectedId] || {}}
                                    myUserId={user?.id}
                                />
                            </div>

                            <div className="border-t border-ink/10 p-2.5 bg-[#f0f2f5] relative safe-bottom">
                                {replyTo && (
                                    <ReplyPreviewBar
                                        quoted={replyTo}
                                        onCancel={() => setReplyTo(null)}
                                    />
                                )}
                                {mentionOpen && filteredMentionOptions.length > 0 && (
                                    <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-ink/20 shadow-lg max-h-48 overflow-y-auto z-10"
                                         data-testid="teamtalk-mention-dropdown">
                                        {filteredMentionOptions.map((m) => (
                                            <button
                                                key={m.id} type="button"
                                                onClick={() => pickMention(m)}
                                                data-testid={`teamtalk-mention-option-${m.id}`}
                                                className={`w-full text-left px-3 py-2 hover:bg-cream/70 flex items-center gap-2 ${
                                                    m._synthetic ? "border-b border-ink/10 bg-cobalt/5" : ""
                                                }`}
                                            >
                                                <At size={12} weight="bold" className={m._synthetic ? "text-cobalt" : "text-cobalt"} />
                                                <span className="text-sm font-bold">
                                                    {m._synthetic ? "all" : m.name}
                                                </span>
                                                <span className={`text-[10px] uppercase ${m._synthetic ? "text-cobalt font-black" : "text-ink/40"}`}>
                                                    {m._synthetic ? "notify everyone" : roleLabel(m.role)}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {mentionRefs.length > 0 && (
                                    <div className="flex items-center gap-1 flex-wrap mb-2 text-[10px]">
                                        <span className="text-ink/40 uppercase font-black tracking-wider">Tagging:</span>
                                        {mentionRefs.map((m) => (
                                            <span
                                                key={m.id}
                                                className="inline-flex items-center gap-1 bg-cobalt/10 text-cobalt px-1.5 py-0.5 font-black uppercase tracking-wider"
                                                data-testid={`teamtalk-mention-tag-${m.id}`}
                                            >
                                                @{m.name}
                                                <button type="button" onClick={() => setMentionRefs((p) => p.filter((x) => x.id !== m.id))}
                                                        className="hover:text-red-600" aria-label="Remove">×</button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="flex gap-2 items-center">
                                    <input
                                        ref={fileInputRef} type="file" className="hidden"
                                        accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                                        onChange={(e) => uploadFile(e.target.files?.[0])}
                                        data-testid="teamtalk-file-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploadBusy}
                                        data-testid="teamtalk-attach-btn"
                                        className="w-10 h-10 shrink-0 inline-flex items-center justify-center rounded-full text-ink/60 hover:text-cobalt hover:bg-black/5 disabled:opacity-40 transition"
                                        aria-label="Attach file"
                                    >
                                        <Paperclip size={20} weight="bold" />
                                    </button>
                                    <textarea
                                        ref={replyInputRef}
                                        value={reply}
                                        rows={1}
                                        onChange={(e) => {
                                            handleReplyChange(e);
                                            // Auto-grow up to ~5 lines
                                            const el = e.currentTarget;
                                            el.style.height = "auto";
                                            el.style.height = Math.min(el.scrollHeight, 120) + "px";
                                        }}
                                        onKeyDown={(e) => {
                                            if (mentionOpen && e.key === "Escape") { setMentionOpen(false); return; }
                                            // V2.0.20 — Enter sends on desktop, newline on mobile.
                                            // Shift+Enter always inserts a newline.
                                            const isTouch = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;
                                            if (e.key === "Enter" && !e.shiftKey && !mentionOpen && !isTouch) {
                                                e.preventDefault();
                                                sendReply();
                                            }
                                        }}
                                        placeholder={isGroup ? "Message — @ to mention" : "Message"}
                                        data-testid="teamtalk-reply-input"
                                        className="flex-1 min-h-[40px] max-h-[120px] px-4 py-2 bg-white text-[14px] leading-[20px] focus:outline-none rounded-2xl shadow-sm placeholder:text-ink/40 resize-none overflow-y-auto"
                                    />
                                    <button
                                        type="button" onClick={sendReply}
                                        disabled={!reply.trim() || sending}
                                        data-testid="teamtalk-reply-send"
                                        className={`btn-gradient inline-flex items-center justify-center gap-1.5 h-10 px-5 shrink-0 text-[11px] font-black uppercase tracking-wider disabled:opacity-40 rounded-full ${reply.trim() ? "" : "hidden"}`}
                                    >
                                        {sending ? "…" : "Send"}
                                        <PaperPlaneTilt size={11} weight="bold" />
                                    </button>
                                    {!reply.trim() && (
                                        <VoiceRecorder
                                            onSend={sendVoice}
                                            disabled={sending || uploadBusy}
                                            onError={(m) => toast.error(m)}
                                            testId="teamtalk-voice-record-btn"
                                        />
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </section>
            </div>

            {composeOpen && (
                <ComposeModal
                    onClose={() => { setComposeOpen(false); setComposeContext(null); }}
                    onCreated={handleThreadCreated}
                    initialContext={composeContext}
                />
            )}
            <ForwardMessageModal
                open={!!forwardMsg}
                onClose={() => setForwardMsg(null)}
                sourceMessage={forwardMsg}
                context="teamtalk"
            />
            {groupComposeOpen && (
                <GroupComposeModal
                    onClose={() => { setGroupComposeOpen(false); setComposeContext(null); }}
                    onCreated={handleThreadCreated}
                    initialContext={composeContext}
                />
            )}
            {manageOpen && convo?.thread?.is_group && (
                <ManageGroupModal
                    thread={convo.thread}
                    members={convo.members}
                    onClose={() => setManageOpen(false)}
                    onChanged={async () => {
                        await loadThreads();
                        await loadConvo(selectedId);
                    }}
                    onLeft={async () => {
                        setManageOpen(false);
                        setSelectedId(null);
                        await loadThreads();
                    }}
                />
            )}
        </div>
    );
}
